import { Injectable, NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, RouterModule, RouterStateSnapshot, Routes } from '@angular/router';
import { Store } from '@ngrx/store';
import { navigate } from 'app/store/common-effects/router.effects';
import { getAssignedProjects } from 'app/store/login/assigned-projects/assigned-projects.actions';
import { assignedProjectsState } from 'app/store/login/assigned-projects/assigned-projects.selectors';
import { getCurrencies } from 'app/store/project/currencies/currencies.actions';
import { CurrenciesState } from 'app/store/project/currencies/currencies.reducer';
import { currenciesState } from 'app/store/project/currencies/currencies.selectors';
import { AuthGuard } from 'core/guards/auth.guard';
import moment from 'moment';
import { PageNotFoundComponent } from 'project/pages/page-not-found/page-not-found.component';
import { Observable, of } from 'rxjs';
import { first } from 'rxjs/operators';
import { CHANGELOG_VERSIONS } from 'shared/modules/skeleton/change-log/change-log.component';
import { ProjectLayoutComponent } from './pages/project/project-layout.component';
import { ProjectsComponent } from './pages/projects/projects.component';
import { MonthsDebtsResolver, ProjectByRoute, ProjectStateResolver } from './project-route-resolvers';

export { MonthsDebtsResolver, ProjectByRoute, ProjectStateResolver } from './project-route-resolvers';

@Injectable({ providedIn: 'root' })
export class NotSawChangelogResolver {
  constructor(private store$: Store) {}

  resolve(): Observable<any> {
    const saw = localStorage.getItem('user-saw');

    const now = moment().format('DD.MM.YYYY');
    const lastRealise = CHANGELOG_VERSIONS[0].date;
    const dayAfterRealise = moment(lastRealise, 'DD.MM.YYYY').add(1, 'day').format('DD.MM.YYYY');

    const realiseIsToday = now === lastRealise || now === dayAfterRealise;
    const result = realiseIsToday && !saw;

    result && this.store$.dispatch(navigate({ path: ['/', 'changelog'] }));

    return of(null);
  }
}

@Injectable({ providedIn: 'root' })
export class AssignedProjectsResolver {
  constructor(private store$: Store) { }

  resolve(_route: ActivatedRouteSnapshot): Observable<any> {
    this.store$.dispatch(getAssignedProjects());

    return this.store$.select(assignedProjectsState).pipe(first(s => s.isLoaded));
  }
}

@Injectable({ providedIn: 'root' })
export class CurrencyResolver {
  constructor(private store$: Store) { }

  resolve(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<CurrenciesState> {
    this.store$.dispatch(getCurrencies());

    return this.store$.select(currenciesState).pipe(first(s => s.isLoaded));
  }
}

const routes: Routes = [
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    resolve: {
      changelog: NotSawChangelogResolver,
      projects: AssignedProjectsResolver,
      currency: CurrencyResolver,
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: ProjectsComponent,
        resolve: {
          // На случай если надо вернуть как было
          // projects: ProjectWithDebtsResolver,
          debts: MonthsDebtsResolver,
        },
        data: { title: 'PAGE_TITLES.dashboard' },
      },
      {
        path: ':route',
        component: ProjectLayoutComponent,
        resolve: {
          project: ProjectByRoute,
          projectData: ProjectStateResolver,
        },
        children: [
          {
            path: '',
            loadChildren: () => import('./modules/order/order.module').then(m => m.OrderModule),
          },
          {
            path: 'progresses',
            loadChildren: () => import('./modules/progresses/progresses.module').then(m => m.ProgressesModule),
          },
          {
            path: 'reconciliation/claims',
            loadChildren: () => import('./modules/reconciliation/claims/claims.module').then(m => m.ClaimsModule),
          },
          {
            path: 'reconciliation/debt',
            loadChildren: () => import('./modules/reconciliation/debt/debt.module').then(m => m.DebtModule),
          },
          {
            path: 'good',
            loadChildren: () => import('./modules/goods/goods.module').then(m => m.GoodsModule),
          },
          {
            path: 'setting/scheduled-tasks',
            loadChildren: () => import('./modules/settings/scheduled-tasks/scheduled-tasks.module').then(m => m.ScheduledTasksModule),
          },
          {
            path: 'setting/notices',
            loadChildren: () => import('./modules/settings/notices/notices.module').then(m => m.NoticesModule),
          },
          {
            path: 'setting/couriers',
            loadChildren: () => import('./modules/settings/couriers/couriers.module').then(m => m.CouriersModule),
          },
          {
            path: 'setting/courier-chains',
            loadChildren: () => import('./modules/settings/courier-chains/courier-chains.module').then(m => m.CourierChainsModule),
          },
          {
            path: 'setting/sequences',
            loadChildren: () => import('./modules/settings/sequences/sequences.module').then(m => m.SequencesModule),
          },
          {
            path: 'setting/documents',
            loadChildren: () => import('./modules/settings/documents/documents.module').then(m => m.DocumentsModule),
          },
          {
            path: 'setting/pools',
            loadChildren: () => import('./modules/settings/pools/pools.module').then(m => m.PoolsModule),
          },
          {
            path: 'setting/extensions',
            loadChildren: () => import('./modules/settings/extension/extension.module').then(m => m.ExtensionModule),
          },
          {
            path: 'setting/statuses',
            loadChildren: () => import('./modules/settings/status/statuses.module').then(m => m.StatusesModule),
          },
          {
            path: 'setting/status-chains',
            loadChildren: () => import('./modules/settings/status-chains/status-chains.module').then(m => m.StatusChainsModule),
          },
          {
            path: 'setting/senders',
            loadChildren: () => import('./modules/settings/senders/senders.module').then(m => m.SendersModule),
          },
          {
            path: 'setting/storehouses',
            loadChildren: () => import('./modules/settings/store-house/store.module').then(m => m.StorehouseModule),
          },
          {
            path: 'setting/current-project',
            loadChildren: () => import('./modules/settings/current-project/current-project.module').then(m => m.CurrentProjectModule),
          },
          {
            path: 'history/printing',
            loadChildren: () => import('./modules/history/printing-history/printing-history.module').then(
              m => m.PrintingHistoryModule,
            ),
          },
          {
            path: 'history/unloading',
            loadChildren: () => import('./modules/history/unloading-history/unloading.module').then(
              m => m.UnloadingModule,
            ),
          },
          {
            path: 'history/remains-transactions',
            loadChildren: () => import('./modules/history/remains-transactions/remains-transactions.module').then(
              m => m.RemainsTransactionsModule,
            ),
          },
          {
            path: 'history/statuses',
            loadChildren: () => import('./modules/history/statuses-history/statuses-history.module').then(
              m => m.StatusesHistoryModule,
            ),
          },
          {
            path: 'history/notices-log',
            loadChildren: () => import('./modules/history/notices-log/notices-log.module').then(m => m.NoticesLogModule),
          },
          {
            path: 'task',
            loadChildren: () => import('../task/task.module').then(m => m.TaskModule),
          },
          {
            path: 'analytic/:projectId/debts',
            loadChildren: () => import('./modules/reconciliation/debts-chart/debts-chart.module').then(
              m => m.DebtsChartModule,
            ),
          },
          {
            path: 'analytic/:projectId/claims',
            loadChildren: () => import('./modules/reconciliation/claims-chart/claims-chart.module').then(
              m => m.ClaimsChartModule,
            ),
          },
          {
            path: '**',
            component: PageNotFoundComponent,
            data: { title: 'PAGE_TITLES.not_found' },
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class ProjectRoutingModule {
}
